import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';

// Comments and Reviews in CMB Chats (Gary, 3 Nov 2026; docs/TODO.md SOCIAL INBOX ON SENTINEL 4b to 4e). The same
// rows as Sentinel's Social inbox and Reputation pages, through the social-inbox and reputation functions.

export interface CommentItem {
  id: string;
  platform: 'facebook' | 'instagram';
  page_id: string;
  post_kind: 'post' | 'ad' | null;
  ad_name: string | null;
  post_text: string | null;
  post_picture_url: string | null;
  post_permalink: string | null;
  author_name: string | null;
  body: string | null;
  commented_at: string;
  reply_text: string | null;
  replied_at: string | null;
  reply_by_name: string | null;
  reply_error: string | null;
  hidden: boolean;
  leo_kind: string | null;
  leo_suggestion: string | null;
  leo_reason: string | null;
  leo_send_at: string | null;
  left_at: string | null;
  left_by_name: string | null;
  replied_by_leo: boolean;
  removed_at: string | null;
  /** A person is on duty for this comment: Leo never sends it by himself (Gary, 3 Nov 2026). */
  leo_off?: boolean;
}

/** A team note or a Tell Leo instruction on a comment or review, shown in its conversation as Chats notes are. */
export interface ItemNote { id: string; item_id: string; kind: 'note' | 'leo'; body: string; author_name: string | null; created_at: string }

export interface ReviewItem {
  id: string;
  platform: 'google' | 'facebook';
  page_id: string;
  reviewer_name: string | null;
  reviewer_photo_url?: string | null;
  stars: number | null;
  recommends: boolean | null;
  body: string | null;
  reviewed_at: string;
  reply_text: string | null;
  replied_at: string | null;
  reply_by_name: string | null;
  reply_state: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
  reply_error: string | null;
  leo_suggestion: string | null;
  leo_reason: string | null;
  removed_at: string | null;
  /** Leo on duty: when his reply sends itself (a 4 or 5 star review, Gary 3 Nov 2026). */
  leo_send_at?: string | null;
  replied_by_leo?: boolean;
  left_at?: string | null;
  left_by_name?: string | null;
}

export interface SocialPageName { id: string; label: string }

async function call<T>(fn: 'social-inbox' | 'reputation', body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    // The function's own words (e.g. "needs the Comments permission") when it sent any.
    const res = (error as { context?: unknown }).context;
    const msg = res instanceof Response ? await res.json().then((j: { error?: string }) => j.error).catch(() => null) : null;
    throw new Error(msg || error.message || 'That did not work. Try again.');
  }
  if ((data as { error?: string } | null)?.error) throw new Error(String((data as { error: string }).error));
  return data as T;
}

/** A comment still needs someone: no reply, not left, not hidden. */
export function waiting(c: CommentItem): boolean {
  return !c.removed_at && !c.reply_text && !c.left_at && !c.hidden;
}

/** A review still open: no reply, not marked done. */
export function reviewWaiting(r: ReviewItem): boolean {
  return !r.removed_at && !r.reply_text && !r.left_at;
}

// The three lists (Gary, 3 Nov 2026, "start again"): Needs you is what a person must answer; Leo is what he
// answered, closed or is about to send; Done is what a person answered or closed.
export type CommentList = 'Needs you' | 'Leo' | 'Done';

export function listOfComment(c: CommentItem): CommentList {
  if (waiting(c)) return c.leo_send_at ? 'Leo' : 'Needs you';
  return c.replied_by_leo || (!c.reply_text && c.left_by_name === 'Leo') ? 'Leo' : 'Done';
}

export function listOfReview(r: ReviewItem): CommentList {
  if (reviewWaiting(r)) return r.leo_send_at ? 'Leo' : 'Needs you';
  return r.replied_by_leo ? 'Leo' : 'Done';
}

/** Local preview (?preview=head_office): sample comments and reviews, never fetched. */
const preview = () => import.meta.env.DEV && typeof sessionStorage !== 'undefined' && sessionStorage.getItem('chats.preview') !== null;
const ago = (m: number) => new Date(Date.now() - m * 60000).toISOString();
const SAMPLE_ITEMS: CommentItem[] = [
  { id: 's1', platform: 'facebook', page_id: 'p1', post_kind: 'ad', ad_name: 'static_cleaned-this-week_v1', post_text: 'When did your mattress last get a proper clean?', post_picture_url: null, post_permalink: null, author_name: null, body: 'How much for a king size?', commented_at: ago(2), reply_text: null, replied_at: null, reply_by_name: null, reply_error: null, hidden: false, leo_kind: 'interest', leo_suggestion: 'Thanks for asking 😊 Every bed is a little different, so WhatsApp us for a personal estimate: https://cleanmybed.com/za/whatsapp/?from=fb', leo_reason: 'Asks for a price', leo_send_at: ago(-8), left_at: null, left_by_name: null, replied_by_leo: false, removed_at: null },
  { id: 's2', platform: 'facebook', page_id: 'p1', post_kind: 'ad', ad_name: 'static_sheets-every-week_v1', post_text: 'You wash the sheets every week. When was the mattress underneath last cleaned?', post_picture_url: null, post_permalink: null, author_name: null, body: 'That fenter in the center is Dada 😊', commented_at: ago(5), reply_text: null, replied_at: null, reply_by_name: null, reply_error: null, hidden: false, leo_kind: 'no_reply', leo_suggestion: null, leo_reason: 'A friend tagging someone', leo_send_at: null, left_at: null, left_by_name: null, replied_by_leo: false, removed_at: null },
  { id: 's3', platform: 'instagram', page_id: 'p2', post_kind: 'post', ad_name: null, post_text: 'Behind the scenes: Tuesday in Hermanus', post_picture_url: null, post_permalink: null, author_name: 'jane.d', body: 'Wow 😳', commented_at: ago(40), reply_text: 'It surprises most people 😊 Questions are welcome on WhatsApp, 063 083 1139.', replied_at: ago(30), reply_by_name: 'Leo', reply_error: null, hidden: false, leo_kind: 'acknowledge', leo_suggestion: null, leo_reason: null, leo_send_at: null, left_at: null, left_by_name: null, replied_by_leo: true, removed_at: null },
];
SAMPLE_ITEMS.push(
  { id: 's4', platform: 'facebook', page_id: 'p1', post_kind: 'post', ad_name: null, post_text: 'Your child\'s teacher called. They keep falling asleep in class.', post_picture_url: null, post_permalink: null, author_name: 'Thandi', body: 'Booked last month and nobody pitched. Still waiting for my refund.', commented_at: ago(12), reply_text: null, replied_at: null, reply_by_name: null, reply_error: null, hidden: false, leo_kind: 'person', leo_suggestion: "I'm sorry, Thandi, that is not the service we want you to have. Please WhatsApp us so we can sort it out with you directly: https://cleanmybed.com/za/whatsapp/?from=fb", leo_reason: 'A complaint about a missed booking and a refund', leo_send_at: null, left_at: null, left_by_name: null, replied_by_leo: false, removed_at: null },
  { id: 's5', platform: 'facebook', page_id: 'p1', post_kind: 'post', ad_name: null, post_text: 'Dust mites live in every mattress.', post_picture_url: null, post_permalink: null, author_name: 'Sipho', body: '@Lindiwe look at this', commented_at: ago(30), reply_text: null, replied_at: null, reply_by_name: null, reply_error: null, hidden: false, leo_kind: 'no_reply', leo_suggestion: null, leo_reason: 'Only tags a friend', leo_send_at: null, left_at: ago(29), left_by_name: 'Leo', replied_by_leo: false, removed_at: null },
);
const SAMPLE_REVIEWS: ReviewItem[] = [
  { id: 'r1', platform: 'google', page_id: 'g1', reviewer_name: 'Genevieve Burns', stars: 5, recommends: null, body: 'Professional service and my beds are dust free.', reviewed_at: ago(60 * 24 * 2), reply_text: null, replied_at: null, reply_by_name: null, reply_state: null, reply_error: null, leo_suggestion: 'Thank you, Genevieve. We are glad the team left your beds feeling fresh 😊', leo_reason: 'Happy review', removed_at: null, leo_send_at: ago(-45) },
  { id: 'r3', platform: 'google', page_id: 'g1', reviewer_name: 'Pieter Botha', stars: 2, recommends: null, body: 'Took two hours longer than they said and the operator was rude.', reviewed_at: ago(90), reply_text: null, replied_at: null, reply_by_name: null, reply_state: null, reply_error: null, leo_suggestion: "Thank you for telling us, Pieter, and we're sorry the visit fell short. We'd like to put it right, so please WhatsApp us on 063 083 1139.", leo_reason: 'Unhappy review', removed_at: null },
  { id: 'r2', platform: 'facebook', page_id: 'p1', reviewer_name: null, stars: null, recommends: true, body: 'Barry is brilliant, great communication.', reviewed_at: ago(60 * 24 * 40), reply_text: 'Thank you so much for the kind words 😊', replied_at: ago(60 * 24 * 39), reply_by_name: 'Gary Harrod', reply_state: null, reply_error: null, leo_suggestion: null, leo_reason: null, removed_at: null },
];

// One copy for the whole app (the list, an open comment and the tab badge), refreshed every minute while anything
// that needs it is on screen.
interface State {
  items: CommentItem[]; reviews: ReviewItem[]; pages: SocialPageName[];
  /** Notes by comment or review id, oldest first. */
  notes: Map<string, ItemNote[]>;
  leoOn: boolean; canAct: boolean; loaded: boolean; error: string | null;
}
let state: State = { items: [], reviews: [], pages: [], notes: new Map(), leoOn: false, canAct: false, loaded: false, error: null };

function byItem(list: ItemNote[]): Map<string, ItemNote[]> {
  const m = new Map<string, ItemNote[]>();
  for (const n of list) m.set(n.item_id, [...(m.get(n.item_id) ?? []), n]);
  return m;
}
const listeners = new Set<() => void>();
let users = 0;
let timer: ReturnType<typeof setInterval> | null = null;
function set(next: Partial<State>) { state = { ...state, ...next }; listeners.forEach((l) => l()); }

async function load(): Promise<void> {
  if (preview()) {
    set({ items: SAMPLE_ITEMS, reviews: SAMPLE_REVIEWS, canAct: true, loaded: true,
      pages: [{ id: 'p1', label: 'CleanMyBed SA (main)' }, { id: 'p2', label: 'Instagram cleanmybed_za' }, { id: 'g1', label: 'Google CleanMyBed SA' }] });
    return;
  }
  try {
    const [c, r] = await Promise.all([
      call<{ items: CommentItem[]; pages: SocialPageName[]; leo_on: boolean; can_act: boolean; notes?: ItemNote[] }>('social-inbox', { op: 'list' }),
      call<{ reviews: ReviewItem[]; pages: SocialPageName[]; notes?: ItemNote[] }>('reputation', { op: 'list' }),
    ]);
    set({ items: c.items.filter((i) => !i.removed_at), reviews: r.reviews.filter((i) => !i.removed_at),
      pages: [...c.pages, ...r.pages], notes: byItem([...(c.notes ?? []), ...(r.notes ?? [])]),
      leoOn: c.leo_on, canAct: c.can_act, error: null, loaded: true });
  } catch (e) {
    set({ error: e instanceof Error ? e.message : 'Could not read the comments', loaded: true });
  }
}

export function useComments(enabled: boolean) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const l = () => force((n) => n + 1);
    listeners.add(l);
    users++;
    if (users === 1) { void load(); timer = setInterval(() => void load(), 60000); }
    return () => {
      listeners.delete(l);
      users--;
      if (users === 0 && timer) { clearInterval(timer); timer = null; }
    };
  }, [enabled]);

  const reload = useCallback(() => load(), []);
  const act = useCallback(async (op: string, id: string, extra: Record<string, unknown> = {}) => {
    const out = await call<{ suggestion?: string | null; reason?: string; kind?: string | null; mentioned?: string[]; not_reached?: string[] }>('social-inbox', { op, id, ...extra });
    await load();
    return out;
  }, []);
  const reviewAct = useCallback(async (op: string, id: string, extra: Record<string, unknown> = {}) => {
    const out = await call<{ suggestion?: string | null; reason?: string; mentioned?: string[]; not_reached?: string[] }>('reputation', { op, id, ...extra });
    await load();
    return out;
  }, []);
  const setDuty = useCallback(async (on: boolean) => {
    await call('social-inbox', { op: 'duty', on });
    set({ leoOn: on });
    await load();
  }, []);

  return { ...state, reload, act, reviewAct, setDuty };
}
